// Per-output schedules â€” list, add, edit, delete.
//
// Backend encoding (mirrors the Flutter app):
//   Weekly           = comma flags "1,0,1,0,1,0,0" (Sun..Sat)
//   FunctionOnTimer  = minutes from midnight to switch ON  (0 = not set)
//   FunctionOffTimer = minutes from midnight to switch OFF (0 = not set)
//   OutputValue      = 1/0
// A schedule can carry an ON time, an OFF time, or both.

import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import {
  Box, Typography, Card, IconButton, Stack, Button, TextField, Chip,
  ToggleButton, ToggleButtonGroup, FormControlLabel, Checkbox, Dialog,
  DialogTitle, DialogContent, DialogActions, CircularProgress, Tooltip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import ScheduleIcon from '@mui/icons-material/Schedule';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import DeviceShell from '../components/DeviceShell';
import {
  getSchedules, addSchedule, updateSchedule, deleteSchedule,
} from '../../../core/api/api';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const toMinutes = (t) => {
  const [hh, mm] = (t || '00:00').split(':').map(Number);
  return (hh || 0) * 60 + (mm || 0);
};
const toTime = (m) => {
  const n = Number(m) || 0;
  return `${String(Math.floor(n / 60)).padStart(2, '0')}:${String(n % 60).padStart(2, '0')}`;
};
const parseWeekly = (w) => (w ? w.split(',').map((f, i) => (f === '1' ? i : -1)).filter((i) => i >= 0) : []);
const buildWeekly = (idxs) => DAYS.map((_, i) => (idxs.includes(i) ? '1' : '0')).join(',');
const formatDays = (idxs) => {
  if (idxs.length === 0) return 'No days';
  if (idxs.length === 7) return 'Every day';
  return idxs.map((i) => DAYS[i]).join(', ');
};

const EMPTY = { id: null, name: '', onEnabled: true, onTime: '08:00', offEnabled: false, offTime: '18:00', days: [0, 1, 2, 3, 4, 5, 6] };

export default function SchedulesScreen() {
  const { uniqueNumber, output } = useParams();
  const { state } = useLocation();
  const out = Number(output);

  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getSchedules(uniqueNumber, out);
      setList(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setList([]);
    } finally {
      setLoading(false);
    }
  }, [uniqueNumber, out]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(EMPTY); setEditorOpen(true); };
  const openEdit = (s) => {
    const on = Number(s.FunctionOnTimer) || 0;
    const off = Number(s.FunctionOffTimer) || 0;
    setForm({
      id: s.SchedulerID,
      name: s.SchedulerName || '',
      onEnabled: on > 0,
      onTime: toTime(on || 8 * 60),
      offEnabled: off > 0,
      offTime: toTime(off || 18 * 60),
      days: parseWeekly(s.Weekly),
    });
    setEditorOpen(true);
  };

  const patch = (p) => setForm((f) => ({ ...f, ...p }));

  const valid = (form.onEnabled || form.offEnabled) && form.days.length > 0;

  const save = async () => {
    if (!valid) return;
    setSaving(true);
    const data = {
      SchedulerName: form.name.trim() || 'Schedule',
      Weekly: buildWeekly(form.days),
      FunctionOnTimer: form.onEnabled ? toMinutes(form.onTime) : 0,
      FunctionOffTimer: form.offEnabled ? toMinutes(form.offTime) : 0,
      OutputValue: form.onEnabled ? 1 : 0,
    };
    try {
      if (form.id != null) {
        await updateSchedule(data, `SchedulerID = ${form.id}`);
      } else {
        await addSchedule({ ...data, UniqueNumber: uniqueNumber, Output: out });
      }
      setEditorOpen(false);
      await load();
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    await deleteSchedule(`SchedulerID = ${id}`);
    await load();
  };

  return (
    <DeviceShell
      title={`Schedules Â· ${state?.name || `Output ${out}`}`}
      actions={(
        <Tooltip title="Add schedule">
          <IconButton color="inherit" onClick={openNew}><AddIcon /></IconButton>
        </Tooltip>
      )}
    >
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}><CircularProgress /></Box>
      ) : (
        <Stack spacing={1.5}>
          {list.length === 0 && (
            <Box sx={{ textAlign: 'center', color: 'text.secondary', mt: 4 }}>
              <ScheduleIcon sx={{ fontSize: 44, opacity: 0.5 }} />
              <Typography sx={{ mt: 1 }}>No schedules yet.</Typography>
            </Box>
          )}

          {list.map((s) => {
            const on = Number(s.FunctionOnTimer) || 0;
            const off = Number(s.FunctionOffTimer) || 0;
            const idxs = parseWeekly(s.Weekly);
            return (
              <Card key={s.SchedulerID} sx={{ p: 2, borderRadius: 3, bgcolor: 'background.paper', border: 1, borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography fontWeight={700} color="text.primary" noWrap>{s.SchedulerName || 'Schedule'}</Typography>
                    <Stack direction="row" spacing={1} sx={{ my: 0.75 }} useFlexGap flexWrap="wrap">
                      {on > 0 && <Chip size="small" color="success" icon={<PowerSettingsNewIcon />} label={`ON ${toTime(on)}`} />}
                      {off > 0 && <Chip size="small" variant="outlined" icon={<PowerSettingsNewIcon />} label={`OFF ${toTime(off)}`} />}
                      {on === 0 && off === 0 && <Chip size="small" label="No time set" />}
                    </Stack>
                    <Typography variant="body2" color="text.secondary">{formatDays(idxs)}</Typography>
                  </Box>
                  <IconButton onClick={() => openEdit(s)}><EditIcon /></IconButton>
                  <IconButton onClick={() => remove(s.SchedulerID)}><DeleteIcon /></IconButton>
                </Box>
              </Card>
            );
          })}
        </Stack>
      )}

      {/* Add / Edit dialog */}
      <Dialog open={editorOpen} onClose={() => setEditorOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{form.id != null ? 'Edit Schedule' : 'New Schedule'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <TextField label="Name" value={form.name} onChange={(e) => patch({ name: e.target.value })} fullWidth />

            <Box>
              <FormControlLabel
                control={<Checkbox checked={form.onEnabled} onChange={(e) => patch({ onEnabled: e.target.checked })} />}
                label="Turn ON at"
              />
              <TextField
                type="time" value={form.onTime} onChange={(e) => patch({ onTime: e.target.value })}
                disabled={!form.onEnabled} size="small" InputLabelProps={{ shrink: true }} sx={{ ml: 1 }}
              />
            </Box>

            <Box>
              <FormControlLabel
                control={<Checkbox checked={form.offEnabled} onChange={(e) => patch({ offEnabled: e.target.checked })} />}
                label="Turn OFF at"
              />
              <TextField
                type="time" value={form.offTime} onChange={(e) => patch({ offTime: e.target.value })}
                disabled={!form.offEnabled} size="small" InputLabelProps={{ shrink: true }} sx={{ ml: 1 }}
              />
            </Box>

            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2" color="text.secondary">Repeat on</Typography>
                <Button size="small" onClick={() => patch({ days: form.days.length === 7 ? [] : [0, 1, 2, 3, 4, 5, 6] })}>
                  {form.days.length === 7 ? 'Clear' : 'Every day'}
                </Button>
              </Box>
              <ToggleButtonGroup
                value={form.days}
                onChange={(_, v) => patch({ days: v })}
                size="small" sx={{ flexWrap: 'wrap', gap: 0.5 }}
              >
                {DAYS.map((d, i) => (
                  <ToggleButton key={d} value={i} sx={{ borderRadius: 2, px: 1.25 }}>{d}</ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>

            {!valid && (
              <Typography variant="caption" color="error">
                Pick at least one day and enable an ON or OFF time.
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditorOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={!valid || saving}>
            {saving ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </DeviceShell>
  );
}
